import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import { 
  Users, UserCog, Plus, Trash2, Search, Loader2, 
  UserPlus, Building, User, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  RefreshCw, KeyRound, UserX, UserCheck, X, Copy, Download, Shield, Truck, ArrowRightLeft,
  Edit, Eye, FileDown, Briefcase, Mail, Crown, Wrench, ScanSearch, BarChart3, Clock, LogOut,
  Sparkles, Layers, ExternalLink
} from 'lucide-react';
import AccessRequestsTab from './AccessRequestsTab';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Pays avec indicatifs téléphoniques - drapeaux gardés car standards internationaux
const COUNTRIES = [
  { code: 'CI', name: 'Côte d\'Ivoire', flag: '🇨🇮', phoneCode: '+225' },
  { code: 'ML', name: 'Mali', flag: '🇲🇱', phoneCode: '+223' },
  { code: 'BF', name: 'Burkina Faso', flag: '🇧🇫', phoneCode: '+226' },
  { code: 'SN', name: 'Sénégal', flag: '🇸🇳', phoneCode: '+221' },
  { code: 'GN', name: 'Guinée', flag: '🇬🇳', phoneCode: '+224' },
  { code: 'NE', name: 'Niger', flag: '🇳🇪', phoneCode: '+227' },
  { code: 'TG', name: 'Togo', flag: '🇹🇬', phoneCode: '+228' },
  { code: 'BJ', name: 'Bénin', flag: '🇧🇯', phoneCode: '+229' },
  { code: 'GH', name: 'Ghana', flag: '🇬🇭', phoneCode: '+233' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬', phoneCode: '+234' },
];

// Configuration des types d'utilisateurs - Design épuré avec couleurs neutres
const USER_TYPES = {
  all: { label: 'Tous', icon: Users, color: 'bg-slate-100 text-slate-700 border-slate-200' },
  client: { label: 'Clients', icon: User, color: 'bg-slate-100 text-slate-700 border-slate-200' },
  internal: { label: 'Staff Interne', icon: Shield, color: 'bg-slate-100 text-slate-700 border-slate-200' },
  driver: { label: 'Chauffeurs', icon: Truck, color: 'bg-slate-100 text-slate-700 border-slate-200' }
};

// Slugs réservés à chaque population (le reste = internal/staff par défaut)
const CLIENT_ROLE_SLUGS = new Set(['client', 'expediteur']);
const DRIVER_ROLE_SLUGS = new Set(['chauffeur', 'driver']);

// Icônes pour les rôles standards (fallback Shield si rôle custom)
const ROLE_ICON_BY_SLUG = {
  admin: Crown,
  client: User,
  expediteur: User,
  chauffeur: Truck,
  driver: Truck,
  commercial: Briefcase,
  lead_commercial: Briefcase,
  ops: Wrench,
  operateur: Wrench,
  lead_ops: UserCog,
  field_ops: Truck,
  lead_field_ops: UserCog,
  finance: BarChart3,
  comptable: BarChart3,
  auditeur: ScanSearch,
};

const inferRoleType = (slug) => {
  const s = (slug || '').toLowerCase();
  if (CLIENT_ROLE_SLUGS.has(s)) return 'client';
  if (DRIVER_ROLE_SLUGS.has(s)) return 'driver';
  return 'internal';
};

// Fallback statique utilisé uniquement si l'API RBAC est indisponible
const FALLBACK_ROLES = [
  { value: 'client', label: 'Client', Icon: User, type: 'client', color: 'border-slate-300 text-slate-600', isSystem: true },
  { value: 'admin', label: 'Administrateur', Icon: Crown, type: 'internal', color: 'border-slate-300 text-slate-600', isSystem: true },
  { value: 'commercial', label: 'Commercial', Icon: Briefcase, type: 'internal', color: 'border-slate-300 text-slate-600', isSystem: true },
  { value: 'chauffeur', label: 'Chauffeur', Icon: Truck, type: 'driver', color: 'border-slate-300 text-slate-600', isSystem: true },
];

const PAGE_SIZES = [10, 20, 50, 100];

const UnifiedUsersManagement = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  
  // Data state
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, clients: 0, internal: 0, drivers: 0, active: 0, inactive: 0 });
  const [accessRequestsCount, setAccessRequestsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filters
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');
  
  // Selection
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isChangeTypeModalOpen, setIsChangeTypeModalOpen] = useState(false);
  const [showBulkStatusDialog, setShowBulkStatusDialog] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showBulkResetDialog, setShowBulkResetDialog] = useState(false);
  const [showBulkRoleDialog, setShowBulkRoleDialog] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  
  // Action states
  const [creating, setCreating] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkNewStatus, setBulkNewStatus] = useState(true);
  const [bulkNewRole, setBulkNewRole] = useState('client');
  
  // Bulk Reset password
  const [useBulkCustomPassword, setUseBulkCustomPassword] = useState(false);
  const [bulkCustomPassword, setBulkCustomPassword] = useState('');
  const [sendEmailAfterReset, setSendEmailAfterReset] = useState(false);
  const [bulkResetResults, setBulkResetResults] = useState(null);
  const [showBulkResetResultsDialog, setShowBulkResetResultsDialog] = useState(false);
  
  // Reset password (single user)
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [useCustomPassword, setUseCustomPassword] = useState(false);
  const [customPassword, setCustomPassword] = useState('');
  
  // Edit user
  const [editUser, setEditUser] = useState(null);

  // Référentiel des postes (Paramètres → Postes)
  const [jobTitles, setJobTitles] = useState([]);

  // RBAC v2 — rôles + packs/templates fetchés dynamiquement
  const [rbacRoles, setRbacRoles] = useState([]);
  const [rbacPacks, setRbacPacks] = useState([]);
  const [rbacLoading, setRbacLoading] = useState(false);
  
  // Companies list for searchable dropdown
  const [companies, setCompanies] = useState([]);
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  
  // Additional notification emails
  const [additionalEmails, setAdditionalEmails] = useState([]);
  const [newNotificationEmail, setNewNotificationEmail] = useState('');
  const [loadingEmails, setLoadingEmails] = useState(false);
  
  // Change type
  const [changeTypeUser, setChangeTypeUser] = useState(null);
  const [newType, setNewType] = useState('client');
  const [newRole, setNewRole] = useState('');
  
  // Create form
  const [newUser, setNewUser] = useState({
    userType: 'client',
    email: '', firstName: '', lastName: '', phone: '+225', password: '',
    country: 'CI',
    role: 'client', accountType: 'particulier', companyName: '',
    internalRole: 'operateur', departement: '', jobTitleId: '',
    licenseNumber: '', vehicleTypes: [],
    sendWelcomeEmail: true, isActive: true
  });

  // Auth headers
  const getAuthHeaders = () => ({
    headers: {
      'Authorization': 'Bearer cookie-auth',
      'Content-Type': 'application/json'
    }
  });

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (activeTab !== 'all') params.append('userType', activeTab);
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (statusFilter !== 'all') params.append('isActive', statusFilter === 'active' ? 'true' : 'false');
      if (accountTypeFilter !== 'all') params.append('accountType', accountTypeFilter);

      const response = await axios.get(`${API_BASE_URL}/api/unified-users?${params}`, getAuthHeaders());

      setUsers(response.data.users);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors du chargement',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, activeTab, roleFilter, statusFilter, accountTypeFilter, toast]);

  // Fetch companies for dropdown
  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/unified-users/companies`, getAuthHeaders());
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchUsers(), 300);
    return () => clearTimeout(timer);
  }, [fetchUsers]);

  // Fetch access request count for tab badge
  useEffect(() => {
    const fetchAccessRequestsCount = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/admin/access-requests/stats`, {});
        setAccessRequestsCount(res.data.pending_approval || 0);
      } catch (err) {
        // Silently fail - not critical
      }
    };
    fetchAccessRequestsCount();
  }, [activeTab]);

  // Load companies when edit modal opens
  useEffect(() => {
    if (isEditModalOpen) {
      fetchCompanies();
    }
  }, [isEditModalOpen, fetchCompanies]);

  // RBAC v2 — fetch dynamique des rôles + packs/templates
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRbacLoading(true);
      try {
        const [rolesRes, packsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/rbac/roles`, getAuthHeaders()),
          axios.get(`${API_BASE_URL}/api/rbac/packs`, getAuthHeaders()),
        ]);
        if (cancelled) return;
        const activeRoles = (rolesRes.data || []).filter(r => r.isActive !== false);
        setRbacRoles(activeRoles);
        setRbacPacks(packsRes.data || []);
      } catch (err) {
        console.error('RBAC fetch error:', err);
        // Fallback silencieux : on garde les rôles statiques
      } finally {
        if (!cancelled) setRbacLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Référentiel des postes — alimente les listes Poste / Département du staff
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/settings/job-titles?active_only=true`, getAuthHeaders());
        setJobTitles(res.data?.jobTitles || []);
      } catch (err) {
        console.error('Job titles fetch error:', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load additional notification emails when edit modal opens
  useEffect(() => {
    if (isEditModalOpen && editUser?.id) {
      fetchAdditionalEmails(editUser.id);
    }
  }, [isEditModalOpen, editUser?.id]);

  // Fetch additional notification emails
  const fetchAdditionalEmails = async (userId) => {
    try {
      setLoadingEmails(true);
      const response = await axios.get(`${API_BASE_URL}/api/unified-users/${userId}/notification-emails`, getAuthHeaders());
      setAdditionalEmails(response.data.additionalEmails || []);
    } catch (error) {
      console.error('Error fetching additional emails:', error);
      setAdditionalEmails([]);
    } finally {
      setLoadingEmails(false);
    }
  };

  // Add notification email
  const handleAddNotificationEmail = async () => {
    if (!newNotificationEmail || !editUser?.id) return;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newNotificationEmail)) {
      toast({ title: 'Erreur', description: 'Format d\'email invalide', variant: 'destructive' });
      return;
    }
    
    try {
      await axios.post(
        `${API_BASE_URL}/api/unified-users/${editUser.id}/notification-emails/add`,
        { email: newNotificationEmail },
        getAuthHeaders()
      );
      setAdditionalEmails([...additionalEmails, newNotificationEmail.toLowerCase()]);
      setNewNotificationEmail('');
      toast({ title: 'Email ajouté', description: `${newNotificationEmail} recevra désormais les notifications` });
    } catch (error) {
      toast({ 
        title: 'Erreur', 
        description: error.response?.data?.detail || 'Erreur lors de l\'ajout', 
        variant: 'destructive' 
      });
    }
  };

  // Remove notification email
  const handleRemoveNotificationEmail = async (email) => {
    if (!editUser?.id) return;
    
    try {
      await axios.delete(
        `${API_BASE_URL}/api/unified-users/${editUser.id}/notification-emails/${encodeURIComponent(email)}`,
        getAuthHeaders()
      );
      setAdditionalEmails(additionalEmails.filter(e => e !== email));
      toast({ title: 'Email supprimé' });
    } catch (error) {
      toast({ 
        title: 'Erreur', 
        description: error.response?.data?.detail || 'Erreur lors de la suppression', 
        variant: 'destructive' 
      });
    }
  };

  // Reset selection on filter change
  useEffect(() => {
    setSelectedUsers([]);
    setSelectAll(false);
  }, [page, pageSize, searchQuery, activeTab, roleFilter, statusFilter, accountTypeFilter]);

  // Selection handlers
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    setSelectedUsers(checked ? users.map(u => ({ id: u.id, type: u.userType })) : []);
  };

  const handleSelectUser = (user, checked) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, { id: user.id, type: user.userType }]);
    } else {
      setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
      setSelectAll(false);
    }
  };

  const isUserSelected = (userId) => selectedUsers.some(u => u.id === userId);

  // Clear filters
  const clearFilters = () => {
    setSearchQuery('');
    setRoleFilter('all');
    setStatusFilter('all');
    setAccountTypeFilter('all');
    setPage(1);
  };

  const hasActiveFilters = searchQuery || roleFilter !== 'all' || statusFilter !== 'all' || accountTypeFilter !== 'all';

  // Create user
  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.firstName || !newUser.lastName || !newUser.password) {
      toast({ title: 'Champs requis', description: 'Veuillez remplir tous les champs obligatoires', variant: 'destructive' });
      return;
    }

    setCreating(true);
    try {
      const payload = { ...newUser };
      if (newUser.userType === 'internal') {
        payload.internalRole = newUser.internalRole;
        if (!newUser.jobTitleId) {
          toast({ title: 'Poste requis', description: "Sélectionnez le poste de la personne dans l'entreprise", variant: 'destructive' });
          setCreating(false);
          return;
        }
      }
      
      const response = await axios.post(`${API_BASE_URL}/api/unified-users`, payload, getAuthHeaders());
      
      toast({ 
        title: 'Utilisateur créé', 
        description: `${newUser.firstName} ${newUser.lastName} a été créé. ${response.data.welcomeEmailSent ? 'Email de bienvenue envoyé.' : ''}`
      });
      
      setNewUser({
        userType: 'client', email: '', firstName: '', lastName: '', phone: '', password: '',
        role: 'client', accountType: 'particulier', companyName: '',
        internalRole: 'operateur', departement: '', jobTitleId: '',
        licenseNumber: '', vehicleTypes: [],
        sendWelcomeEmail: true, isActive: true
      });
      setIsCreateModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Erreur lors de la création', variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  // Edit user
  const handleEditUser = async () => {
    if (!editUser) return;
    setBulkActionLoading(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/api/unified-users/${editUser.id}`,
        {
          userType: editUser.userType,
          firstName: editUser.firstName,
          lastName: editUser.lastName,
          phone: editUser.phone,
          departement: editUser.departement,
          jobTitleId: editUser.userType === 'internal' ? (editUser.jobTitleId || '') : undefined,
          manager_id: editUser.userType === 'internal' ? (editUser.manager_id || '') : undefined,
          companyName: editUser.companyName,
          accountType: editUser.accountType || 'particulier',
          role: editUser.role
        },
        getAuthHeaders()
      );
      
      toast({ title: 'Utilisateur modifié' });
      setIsEditModalOpen(false);
      setEditUser(null);
      fetchUsers();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail, variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Individual role change
  const handleRoleChange = async (user, newRoleValue) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/api/unified-users/${user.id}/role?user_type=${user.userType}`,
        { role: newRoleValue },
        getAuthHeaders()
      );
      
      const roleConfig = dynamicRoles.find(r => r.value === newRoleValue);
      toast({ title: 'Rôle modifié', description: `Rôle changé en ${roleConfig?.label || newRoleValue}` });
      fetchUsers();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail, variant: 'destructive' });
    }
  };

  // Bulk role change
  const handleBulkRoleChange = async () => {
    if (selectedUsers.length === 0) return;
    setBulkActionLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/unified-users/bulk/role`,
        { users: selectedUsers, newRole: bulkNewRole },
        getAuthHeaders()
      );
      
      toast({ title: 'Rôles modifiés', description: `${selectedUsers.length} utilisateur(s) mis à jour` });
      setShowBulkRoleDialog(false);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail, variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk status change
  const handleBulkStatusChange = async (activate) => {
    if (selectedUsers.length === 0) return;
    setBulkActionLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/unified-users/bulk/status`,
        { userIds: selectedUsers.map(u => u.id), userType: 'all', isActive: activate },
        getAuthHeaders()
      );
      toast({ title: activate ? 'Utilisateurs activés' : 'Utilisateurs désactivés' });
      setShowBulkStatusDialog(false);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail, variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) return;
    setBulkActionLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/unified-users/bulk/delete`,
        { users: selectedUsers },
        getAuthHeaders()
      );
      
      toast({ title: 'Utilisateurs supprimés', description: `${selectedUsers.length} utilisateur(s) supprimé(s)` });
      setShowBulkDeleteDialog(false);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail, variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk reset password
  const handleBulkResetPasswords = async () => {
    if (selectedUsers.length === 0) return;
    
    // Validate custom password if used
    if (useBulkCustomPassword && bulkCustomPassword.length < 6) {
      toast({ title: 'Erreur', description: 'Le mot de passe doit contenir au moins 6 caractères', variant: 'destructive' });
      return;
    }
    
    setBulkActionLoading(true);
    try {
      const payload = { 
        users: selectedUsers.map(u => ({ id: u.id, type: u.type })),
        sendEmail: sendEmailAfterReset
      };
      
      // Add custom password if defined
      if (useBulkCustomPassword && bulkCustomPassword) {
        payload.customPassword = bulkCustomPassword;
      }
      
      const response = await axios.post(
        `${API_BASE_URL}/api/unified-users/bulk/reset-password`,
        payload,
        getAuthHeaders()
      );
      
      // Store results for confirmation modal
      setBulkResetResults({
        results: response.data.results,
        emailsSent: response.data.emailsSent || 0,
        emailsFailed: response.data.emailsFailed || 0,
        usedCustomPassword: useBulkCustomPassword
      });
      
      setShowBulkResetDialog(false);
      setShowBulkResetResultsDialog(true);
      
      toast({ 
        title: 'Mots de passe réinitialisés', 
        description: response.data.message
      });
      
      setSelectedUsers([]);
      setUseBulkCustomPassword(false);
      setBulkCustomPassword('');
      setSendEmailAfterReset(false);
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail, variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  // Download bulk reset results as CSV
  const downloadBulkResetCSV = () => {
    if (!bulkResetResults?.results) return;
    const csvContent = bulkResetResults.results.map(r => 
      `${r.email},${r.newPassword},${r.name},${r.type},${r.emailSent ? 'Oui' : 'Non'}`
    ).join('\n');
    const blob = new Blob([`Email,Nouveau mot de passe,Nom,Type,Email envoyé\n${csvContent}`], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `passwords_reset_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast({ title: 'CSV téléchargé' });
  };
  
  // Copy all passwords to clipboard
  const copyAllPasswordsToClipboard = () => {
    if (!bulkResetResults?.results) return;
    const text = bulkResetResults.results.map(r => 
      `${r.email}: ${r.newPassword}`
    ).join('\n');
    navigator.clipboard.writeText(text);
    toast({ title: 'Copié dans le presse-papier' });
  };

  // Single password reset
  const handleResetPassword = async () => {
    if (!resetPasswordUser) return;
    setBulkActionLoading(true);
    try {
      const payload = { users: [{ id: resetPasswordUser.id, type: resetPasswordUser.userType }] };
      if (useCustomPassword && customPassword) {
        payload.customPassword = customPassword;
      }
      
      const response = await axios.post(
        `${API_BASE_URL}/api/unified-users/bulk/reset-password`,
        payload,
        getAuthHeaders()
      );
      
      setResetPasswordResult(response.data.results[0]);
      toast({ title: 'Mot de passe réinitialisé' });
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail, variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Change user type
  const handleChangeType = async () => {
    if (!changeTypeUser) return;
    setBulkActionLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/unified-users/change-type`,
        {
          userId: changeTypeUser.id,
          currentType: changeTypeUser.userType,
          newType: newType,
          newRole: newRole || null
        },
        getAuthHeaders()
      );
      
      toast({ title: 'Type modifié', description: `Utilisateur converti en ${USER_TYPES[newType].label}` });
      setIsChangeTypeModalOpen(false);
      setChangeTypeUser(null);
      fetchUsers();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail, variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Delete user
  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Supprimer ${user.firstName} ${user.lastName} ?`)) return;
    
    try {
      await axios.delete(
        `${API_BASE_URL}/api/unified-users/${user.id}?user_type=${user.userType}`,
        getAuthHeaders()
      );
      toast({ title: 'Utilisateur supprimé' });
      fetchUsers();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail, variant: 'destructive' });
    }
  };

  // Resend activation email
  const handleResendActivation = async (user) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/unified-users/${user.id}/resend-activation`,
        { userType: user.userType },
        getAuthHeaders()
      );
      toast({ 
        title: 'Email envoyé', 
        description: response.data.message || `Email d'activation renvoyé à ${user.email}` 
      });
    } catch (error) {
      toast({ 
        title: 'Erreur', 
        description: error.response?.data?.detail || "Échec de l'envoi de l'email", 
        variant: 'destructive' 
      });
    }
  };

  // Révoquer toutes les sessions actives d'un utilisateur (fix sécurité P2)
  const handleRevokeSessions = async (user) => {
    const ok = window.confirm(
      `Révoquer toutes les sessions actives de ${user.firstName} ${user.lastName} (${user.email}) ?\n\n` +
      `L'utilisateur sera déconnecté de tous ses appareils et devra se reconnecter.\n\n` +
      `Cette action est recommandée en cas de compte compromis ou départ d'un collaborateur.`
    );
    if (!ok) return;
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/users/${user.id}/revoke-sessions`,
        {},
        getAuthHeaders()
      );
      toast({
        title: 'Sessions révoquées',
        description: `${user.email} a été déconnecté de tous ses appareils.`,
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de révoquer les sessions',
        variant: 'destructive',
      });
    }
  };

  // Export users to CSV
  const handleExportUsers = async () => {
    try {
      const usersToExport = selectedUsers.length > 0 
        ? users.filter(u => selectedUsers.some(s => s.id === u.id))
        : users;
      
      const csvHeader = 'Type,Nom,Prénom,Email,Téléphone,Rôle,Statut,Département,Entreprise,Date création\n';
      const csvContent = usersToExport.map(u => 
        `${u.userType},${u.lastName},${u.firstName},${u.email},${u.phone || ''},${u.displayRole || u.role},${u.isActive ? 'Actif' : 'Inactif'},${u.departement || ''},${u.companyName || ''},${u.createdAt || ''}`
      ).join('\n');
      
      const blob = new Blob([csvHeader + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `utilisateurs_export_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      
      toast({ title: 'Export réussi', description: `${usersToExport.length} utilisateur(s) exporté(s)` });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Erreur lors de l\'export', variant: 'destructive' });
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copié' });
  };

  // RBAC v2 — Construit la liste de rôles à afficher (dynamique + fallback)
  const dynamicRoles = useMemo(() => {
    if (!rbacRoles || rbacRoles.length === 0) return FALLBACK_ROLES;
    return rbacRoles.map(r => {
      // Disambiguation : 'operateur' (legacy) et 'ops' (canonique RBAC v2) ont le
      // même nom côté DB. On suffixe le legacy pour éviter les doublons visibles.
      const isLegacyOperateur = (r.slug || '').toLowerCase() === 'operateur';
      const label = isLegacyOperateur ? `${r.name} (legacy)` : r.name;
      return {
        value: r.slug,
        label,
        Icon: ROLE_ICON_BY_SLUG[r.slug] || (inferRoleType(r.slug) === 'driver' ? Truck : Shield),
        type: inferRoleType(r.slug),
        color: 'border-slate-300 text-slate-600',
        isSystem: !!r.isSystem,
        description: r.description,
        permissionCount: (r.permissions || []).length,
      };
    }).sort((a, b) => {
      // Système d'abord, puis alpha
      if (a.isSystem !== b.isSystem) return a.isSystem ? -1 : 1;
      return a.label.localeCompare(b.label);
    });
  }, [rbacRoles]);

  const dynamicRolesByType = useMemo(() => {
    const groups = { client: [], internal: [], driver: [] };
    dynamicRoles.forEach(r => { groups[r.type]?.push(r); });
    return groups;
  }, [dynamicRoles]);

  // Départements connus = ceux du référentiel de postes + ceux déjà saisis sur les fiches
  const departmentOptions = useMemo(() => {
    const set = new Set();
    jobTitles.forEach((t) => { if (t.department) set.add(t.department.trim()); });
    users.forEach((u) => { if (u.departement) set.add(u.departement.trim()); });
    return [...set].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [jobTitles, users]);

  // Postes proposés : ceux du département choisi d'abord, puis tous les autres
  const jobTitlesForEdit = useMemo(() => {
    const dept = (editUser?.departement || '').trim();
    if (!dept) return jobTitles;
    return [
      ...jobTitles.filter((t) => (t.department || '').trim() === dept),
      ...jobTitles.filter((t) => (t.department || '').trim() !== dept),
    ];
  }, [jobTitles, editUser?.departement]);


  // Get available roles based on current filter
  const getAvailableRoles = () => {
    if (activeTab === 'all') return dynamicRoles;
    return dynamicRolesByType[activeTab] || dynamicRoles;
  };

  // Get user type badge - Design épuré
  const getUserTypeBadge = (userType) => {
    const config = USER_TYPES[userType] || USER_TYPES.client;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${config.color} border`}>
        <Icon className="w-3 h-3 mr-1.5 text-slate-500" />
        {config.label}
      </span>
    );
  };

  // Get role badge - Design épuré avec outline
  const getRoleBadge = (role, userType) => {
    const roleConfig = dynamicRoles.find(r => r.value === role)
      || FALLBACK_ROLES.find(r => r.value === role)
      || { value: role, label: role, Icon: User, color: 'border-slate-300 text-slate-600', isSystem: true };
    const IconComp = roleConfig.Icon || User;
    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-white border ${roleConfig.color}`}
        title={roleConfig.description || roleConfig.label}
      >
        <IconComp className="w-3 h-3 mr-1.5" />
        {roleConfig.label}
        {roleConfig.isSystem === false && (
          <span className="ml-1.5 px-1 py-0 text-[9px] uppercase rounded bg-violet-100 text-violet-700 border border-violet-200">
            Custom
          </span>
        )}
      </span>
    );
  };

  // Loading state
  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#1db584] mx-auto" />
          <p className="mt-4 text-gray-600">Chargement des utilisateurs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-lg md:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <UserCog className="w-7 h-7 text-[#1db584]" />
              Gestion des Utilisateurs
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              {stats.total} utilisateur(s) • {stats.clients} clients • {stats.internal} staff • {stats.drivers} chauffeurs
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportUsers}>
              <FileDown className="w-4 h-4 mr-2" />
              Exporter
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/settings?tab=audit')}>
              <Eye className="w-4 h-4 mr-2" />
              Logs d&apos;audit
            </Button>
            <Button onClick={() => setIsCreateModalOpen(true)} className="bg-[#1db584] hover:bg-[#19a074]">
              <Plus className="w-4 h-4 mr-2" />
              Nouvel utilisateur
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <Users className="w-6 h-6 mx-auto text-gray-500 mb-1" />
              <p className="text-xs text-gray-500">Total</p>
              <p className="text-xl font-bold">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <User className="w-6 h-6 mx-auto text-blue-500 mb-1" />
              <p className="text-xs text-gray-500">Clients</p>
              <p className="text-xl font-bold text-blue-600">{stats.clients}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <Shield className="w-6 h-6 mx-auto text-purple-500 mb-1" />
              <p className="text-xs text-gray-500">Staff</p>
              <p className="text-xl font-bold text-purple-600">{stats.internal}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <Truck className="w-6 h-6 mx-auto text-green-500 mb-1" />
              <p className="text-xs text-gray-500">Chauffeurs</p>
              <p className="text-xl font-bold text-green-600">{stats.drivers}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <UserCheck className="w-6 h-6 mx-auto text-emerald-500 mb-1" />
              <p className="text-xs text-gray-500">Actifs</p>
              <p className="text-xl font-bold text-emerald-600">{stats.active}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <UserX className="w-6 h-6 mx-auto text-gray-400 mb-1" />
              <p className="text-xs text-gray-500">Inactifs</p>
              <p className="text-xl font-bold text-gray-500">{stats.inactive}</p>
            </div>
          </CardContent>
        </Card>
        {accessRequestsCount > 0 && (
          <Card className="border-amber-200 bg-amber-50/50 cursor-pointer" onClick={() => setActiveTab('access_requests')} data-testid="access-requests-stat-card">
            <CardContent className="pt-4 pb-4">
              <div className="text-center">
                <Clock className="w-6 h-6 mx-auto text-amber-500 mb-1" />
                <p className="text-xs text-gray-500">Demandes</p>
                <p className="text-xl font-bold text-amber-600">{accessRequestsCount}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabs + Filters */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setRoleFilter('all'); setPage(1); }}>
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
          <TabsList className="grid w-full md:w-auto grid-cols-5">
            <TabsTrigger value="all">Tous</TabsTrigger>
            <TabsTrigger value="client">Clients</TabsTrigger>
            <TabsTrigger value="internal">Staff</TabsTrigger>
            <TabsTrigger value="driver">Chauffeurs</TabsTrigger>
            <TabsTrigger value="access_requests" className="relative" data-testid="access-requests-tab">
              Demandes
              {accessRequestsCount > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 min-w-[20px] px-1 text-xs">{accessRequestsCount}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          {activeTab !== 'access_requests' && (
          <div className="flex flex-1 gap-2 flex-wrap">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Rechercher par nom, email, téléphone..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                className="pl-10"
              />
            </div>
            
            <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Rôle" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les rôles</SelectItem>
                {getAvailableRoles().map(role => {
                  const Ic = role.Icon || User;
                  return (
                    <SelectItem key={role.value} value={role.value}>
                      <span className="inline-flex items-center gap-2">
                        <Ic className="w-3.5 h-3.5 text-slate-500" />
                        {role.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[130px]"><SelectValue placeholder="Statut" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="inactive">Inactifs</SelectItem>
              </SelectContent>
            </Select>

            {activeTab === 'client' && (
              <Select value={accountTypeFilter} onValueChange={(v) => { setAccountTypeFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[150px]"><SelectValue placeholder="Type compte" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="particulier">Particulier</SelectItem>
                  <SelectItem value="entreprise">Entreprise</SelectItem>
                </SelectContent>
              </Select>
            )}
            
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />Effacer
              </Button>
            )}
            
            <Button variant="outline" size="icon" onClick={fetchUsers}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          )}
        </div>

        {/* Bulk Actions Bar */}
        {activeTab !== 'access_requests' && selectedUsers.length > 0 && (
          <Card className="mb-4 border-[#1db584] bg-[#1db584]/5">
            <CardContent className="py-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="font-medium text-[#1db584]">
                  {selectedUsers.length} utilisateur(s) sélectionné(s)
                </span>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => { setBulkNewStatus(true); setShowBulkStatusDialog(true); }} className="text-green-600 border-green-300">
                    <UserCheck className="w-4 h-4 mr-1" />Activer
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setBulkNewStatus(false); setShowBulkStatusDialog(true); }} className="text-orange-600 border-orange-300">
                    <UserX className="w-4 h-4 mr-1" />Désactiver
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowBulkRoleDialog(true)} className="text-purple-600 border-purple-300">
                    <Shield className="w-4 h-4 mr-1" />Changer rôle
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowBulkResetDialog(true)} className="text-blue-600 border-blue-300">
                    <KeyRound className="w-4 h-4 mr-1" />Reset MDP
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleExportUsers} className="text-gray-600 border-gray-300">
                    <FileDown className="w-4 h-4 mr-1" />Exporter
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowBulkDeleteDialog(true)} className="text-red-600 border-red-300">
                    <Trash2 className="w-4 h-4 mr-1" />Supprimer
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setSelectedUsers([])}>
                    <X className="w-4 h-4 mr-1" />Annuler
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Users Table */}
        {activeTab !== 'access_requests' && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <Checkbox checked={selectAll} onCheckedChange={handleSelectAll} />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilisateur</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rôle</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                    {activeTab === 'internal' && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Poste / Département</th>
                    )}
                    {activeTab === 'client' && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entreprise</th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {users.map((user) => (
                    <tr key={`${user.userType}-${user.id}`} className={`hover:bg-gray-50 ${isUserSelected(user.id) ? 'bg-[#1db584]/5' : ''}`}>
                      <td className="px-4 py-3">
                        <Checkbox 
                          checked={isUserSelected(user.id)} 
                          onCheckedChange={(checked) => handleSelectUser(user, checked)} 
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{user.firstName} {user.lastName}</p>
                          <p className="text-xs text-gray-500">{user.clientNumber || user.staffNumber || user.driverCode || '-'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getUserTypeBadge(user.userType)}
                      </td>
                      <td className="px-4 py-3">
                        <Select 
                          value={user.displayRole || user.role} 
                          onValueChange={(newRoleValue) => handleRoleChange(user, newRoleValue)}
                        >
                          <SelectTrigger className="w-[140px] h-8">
                            <SelectValue>
                              {getRoleBadge(user.displayRole || user.role, user.userType)}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {(dynamicRolesByType[user.userType] || []).map(r => {
                              const Ic = r.Icon || User;
                              return (
                                <SelectItem key={r.value} value={r.value}>
                                  <span className="inline-flex items-center gap-2">
                                    <Ic className="w-3.5 h-3.5 text-slate-500" />
                                    {r.label}
                                    {!r.isSystem && (
                                      <span className="ml-1 px-1 text-[9px] uppercase rounded bg-violet-100 text-violet-700">Custom</span>
                                    )}
                                  </span>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm text-gray-900">{user.email}</p>
                          <p className="text-xs text-gray-500">{user.phone || '-'}</p>
                        </div>
                      </td>
                      {activeTab === 'internal' && (
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-900">
                            {user.jobTitle || <span className="text-xs text-amber-600">Poste à renseigner</span>}
                          </p>
                          <p className="text-xs text-gray-500">{user.departement || '-'}</p>
                        </td>
                      )}
                      {activeTab === 'client' && (
                        <td className="px-4 py-3">
                          <div>
                            {user.accountType === 'entreprise' && user.companyName ? (
                              <div className="flex items-center gap-1">
                                <Building className="w-3 h-3 text-gray-400" />
                                <span className="text-sm text-gray-600">{user.companyName}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">Particulier</span>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="px-4 py-3">
                        {user.isActive !== false ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5"></span>
                            Actif
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 border border-slate-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mr-1.5"></span>
                            Inactif
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                            title="Modifier"
                            data-testid={`edit-user-${user.id}`}
                            onClick={() => { setEditUser({...user}); setCompanySearchQuery(''); setNewNotificationEmail(''); setAdditionalEmails([]); setIsEditModalOpen(true); }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {/* Bouton renvoi email d'activation - visible si compte non vérifié */}
                          {user.isVerified === false && (
                            <Button 
                              variant="ghost" size="sm" className="h-8 w-8 p-0 text-amber-500 hover:text-amber-600 hover:bg-amber-50"
                              title="Renvoyer l'email d'activation"
                              onClick={() => handleResendActivation(user)}
                            >
                              <Mail className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                            title="Réinitialiser le mot de passe"
                            onClick={() => { setResetPasswordUser(user); setResetPasswordResult(null); setUseCustomPassword(false); setCustomPassword(''); setShowResetPasswordModal(true); }}
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                            title="Changer le type"
                            onClick={() => { setChangeTypeUser(user); setNewType(user.userType === 'client' ? 'internal' : 'client'); setIsChangeTypeModalOpen(true); }}
                          >
                            <ArrowRightLeft className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" size="sm" className="h-8 w-8 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            title="Révoquer toutes les sessions (déconnexion forcée)"
                            onClick={() => handleRevokeSessions(user)}
                            data-testid={`revoke-sessions-btn-${user.id}`}
                          >
                            <LogOut className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
                            title="Supprimer"
                            onClick={() => handleDeleteUser(user)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {users.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Aucun utilisateur trouvé</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Afficher</span>
                  <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(parseInt(v)); setPage(1); }}>
                    <SelectTrigger className="w-[70px] h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZES.map(size => <SelectItem key={size} value={size.toString()}>{size}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <span>par page • {total} résultat(s)</span>
                </div>
                
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" onClick={() => setPage(1)} disabled={page === 1}>
                    <ChevronsLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="px-3 text-sm">Page {page} / {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage(totalPages)} disabled={page >= totalPages}>
                    <ChevronsRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        )}
        
        {/* Access Requests Tab Content */}
        {activeTab === 'access_requests' && (
          <AccessRequestsTab currentUserRole={JSON.parse(localStorage.getItem('baobab_user') || '{}').role} />
        )}
      </Tabs>

      {/* Create User Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#1db584]" />
              Créer un nouvel utilisateur
            </DialogTitle>
            <DialogDescription>
              Choisissez le type et remplissez les informations
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {/* Type Selection */}
            <div className="space-y-2">
              <Label>Type d&apos;utilisateur *</Label>
              <div className="flex gap-2">
                {['client', 'internal', 'driver'].map(type => (
                  <Button 
                    key={type}
                    type="button" 
                    variant={newUser.userType === type ? 'default' : 'outline'}
                    className={newUser.userType === type ? 'bg-[#1db584] hover:bg-[#19a074]' : ''}
                    onClick={() => setNewUser({...newUser, userType: type})}
                  >
                    {React.createElement(USER_TYPES[type].icon, { className: "w-4 h-4 mr-2" })}
                    {USER_TYPES[type].label}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Common Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Prénom *</Label>
                <Input id="firstName" value={newUser.firstName} onChange={(e) => setNewUser({...newUser, firstName: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Nom *</Label>
                <Input id="lastName" value={newUser.lastName} onChange={(e) => setNewUser({...newUser, lastName: e.target.value})} />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="country">Pays *</Label>
              <Select value={newUser.country} onValueChange={(v) => {
                const country = COUNTRIES.find(c => c.code === v);
                const currentPhone = newUser.phone || '';
                // Remplacer l'ancien indicatif par le nouveau
                let newPhone = currentPhone;
                const oldCountry = COUNTRIES.find(c => c.code === newUser.country);
                if (oldCountry && currentPhone.startsWith(oldCountry.phoneCode)) {
                  newPhone = country.phoneCode + currentPhone.slice(oldCountry.phoneCode.length);
                } else if (!currentPhone || currentPhone === '') {
                  newPhone = country.phoneCode;
                }
                setNewUser({...newUser, country: v, phone: newPhone});
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un pays" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.flag} {country.name} ({country.phoneCode})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Téléphone</Label>
              <div className="flex">
                <div className="flex items-center px-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md text-sm text-gray-600">
                  {COUNTRIES.find(c => c.code === newUser.country)?.flag} {COUNTRIES.find(c => c.code === newUser.country)?.phoneCode || '+225'}
                </div>
                <Input 
                  id="phone" 
                  value={newUser.phone?.replace(COUNTRIES.find(c => c.code === newUser.country)?.phoneCode || '+225', '') || ''} 
                  onChange={(e) => {
                    const phoneCode = COUNTRIES.find(c => c.code === newUser.country)?.phoneCode || '+225';
                    const phoneNumber = e.target.value.replace(/[^0-9]/g, '');
                    setNewUser({...newUser, phone: phoneCode + phoneNumber});
                  }} 
                  className="rounded-l-none"
                  placeholder="XX XX XX XX XX"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe *</Label>
              <Input id="password" type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} placeholder="Minimum 6 caractères" />
            </div>

            {/* Client-specific Fields */}
            {newUser.userType === 'client' && (
              <>
                <div className="space-y-2">
                  <Label>Type de compte</Label>
                  <div className="flex gap-2">
                    <Button type="button" variant={newUser.accountType === 'particulier' ? 'default' : 'outline'} className={newUser.accountType === 'particulier' ? 'bg-[#1db584]' : ''} onClick={() => setNewUser({...newUser, accountType: 'particulier'})}>
                      <User className="w-4 h-4 mr-2" />Particulier
                    </Button>
                    <Button type="button" variant={newUser.accountType === 'entreprise' ? 'default' : 'outline'} className={newUser.accountType === 'entreprise' ? 'bg-[#1db584]' : ''} onClick={() => setNewUser({...newUser, accountType: 'entreprise'})}>
                      <Building className="w-4 h-4 mr-2" />Entreprise
                    </Button>
                  </div>
                </div>
                
                {newUser.accountType === 'entreprise' && (
                  <div className="space-y-2">
                    <Label htmlFor="companyName">Nom de l&apos;entreprise</Label>
                    <Input id="companyName" value={newUser.companyName} onChange={(e) => setNewUser({...newUser, companyName: e.target.value})} />
                  </div>
                )}
              </>
            )}

            {/* Internal-specific Fields */}
            {newUser.userType === 'internal' && (
              <>
                <div className="space-y-2">
                  <Label>Rôle interne *</Label>
                  <Select value={newUser.internalRole} onValueChange={(v) => setNewUser({...newUser, internalRole: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(dynamicRolesByType.internal || []).map(r => {
                        const Ic = r.Icon || Shield;
                        return (
                          <SelectItem key={r.value} value={r.value}>
                            <span className="inline-flex items-center gap-2">
                              <Ic className="w-3.5 h-3.5 text-slate-500" />
                              {r.label}
                              {!r.isSystem && (
                                <span className="ml-1 px-1 text-[9px] uppercase rounded bg-violet-100 text-violet-700">Custom</span>
                              )}
                              <span className="text-[10px] text-slate-400">· {r.permissionCount} perms</span>
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500">
                    {rbacLoading ? 'Chargement des rôles…' : `${(dynamicRolesByType.internal || []).length} rôles disponibles (système + custom)`}
                  </p>
                </div>

                {/* Templates de rôles (packs) — création rapide depuis un template prédéfini */}
                {rbacPacks.length > 0 && (
                  <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      <Label className="text-sm font-medium text-slate-700 m-0">
                        Templates de rôles par population
                      </Label>
                    </div>
                    <p className="text-[11px] text-slate-500 -mt-1">
                      Aucun rôle existant ne correspond ? Créez-en un nouveau à partir d'un template prédéfini.
                    </p>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {rbacPacks.map(p => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => navigate(`/admin/roles?fromPack=${encodeURIComponent(p.slug)}`)}
                          className="flex items-start gap-2 px-2.5 py-2 rounded-md border border-slate-200 bg-white hover:border-emerald-400 hover:bg-emerald-50/40 transition text-left"
                          data-testid={`tpl-pack-${p.slug}`}
                        >
                          <Layers className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-[12px] font-medium text-slate-800 truncate flex items-center gap-1">
                              {p.name}
                              <ExternalLink className="w-3 h-3 text-slate-400" />
                            </div>
                            <div className="text-[10px] text-slate-500 truncate">{(p.permissions || []).length} permissions</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Département</Label>
                  <Select
                    value={newUser.departement || '__none__'}
                    onValueChange={(v) => setNewUser({ ...newUser, departement: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger data-testid="create-staff-department-select">
                      <SelectValue placeholder="Sélectionner un département" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sans département</SelectItem>
                      {departmentOptions.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Poste dans l&apos;entreprise *
                  </Label>
                  <Select
                    value={newUser.jobTitleId || ''}
                    onValueChange={(v) => {
                      const jt = jobTitles.find((t) => t.id === v);
                      setNewUser({
                        ...newUser,
                        jobTitleId: v,
                        departement: !newUser.departement && jt?.department ? jt.department : newUser.departement,
                      });
                    }}
                  >
                    <SelectTrigger data-testid="create-staff-jobtitle-select">
                      <SelectValue placeholder="Sélectionner un poste" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {jobTitles
                        .filter((t) => !newUser.departement || (t.department || '') === newUser.departement)
                        .map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.label}{t.department ? ` · ${t.department}` : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500">
                    Fonction réelle dans l&apos;organigramme, sans effet sur les droits d&apos;accès.
                  </p>
                </div>
              </>
            )}

            {/* Driver-specific Fields */}
            {newUser.userType === 'driver' && (
              <div className="space-y-2">
                <Label htmlFor="licenseNumber">Numéro de permis</Label>
                <Input id="licenseNumber" value={newUser.licenseNumber} onChange={(e) => setNewUser({...newUser, licenseNumber: e.target.value})} />
              </div>
            )}

            {/* Options */}
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox id="sendWelcome" checked={newUser.sendWelcomeEmail} onCheckedChange={(c) => setNewUser({...newUser, sendWelcomeEmail: c})} />
              <Label htmlFor="sendWelcome" className="text-sm">Envoyer un email de bienvenue avec les identifiants</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>Annuler</Button>
            <Button onClick={handleCreateUser} disabled={creating} className="bg-[#1db584] hover:bg-[#19a074]">
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Création...</> : <><Plus className="w-4 h-4 mr-2" />Créer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={(open) => { setIsEditModalOpen(open); if (!open) { setCompanySearchQuery(''); setShowCompanyDropdown(false); } }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#1db584]" />
              Modifier l&apos;utilisateur
            </DialogTitle>
            <DialogDescription>
              {editUser && `${editUser.firstName} ${editUser.lastName} (${editUser.email})`}
            </DialogDescription>
          </DialogHeader>
          
          {editUser && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom</Label>
                  <Input value={editUser.firstName} onChange={(e) => setEditUser({...editUser, firstName: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input value={editUser.lastName} onChange={(e) => setEditUser({...editUser, lastName: e.target.value})} />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input value={editUser.phone || ''} onChange={(e) => setEditUser({...editUser, phone: e.target.value})} />
              </div>
              
              {editUser.userType === 'internal' && (
                <>
                  <div className="space-y-2">
                    <Label>Département</Label>
                    <Select
                      value={editUser.departement || '__none__'}
                      onValueChange={(v) => setEditUser({ ...editUser, departement: v === '__none__' ? '' : v })}
                    >
                      <SelectTrigger data-testid="edit-staff-department-select">
                        <SelectValue placeholder="Sélectionner un département" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Sans département</SelectItem>
                        {departmentOptions.map((d) => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      Poste dans l&apos;entreprise
                    </Label>
                    <Select
                      value={editUser.jobTitleId || '__none__'}
                      onValueChange={(v) => {
                        const jt = jobTitles.find((t) => t.id === v);
                        setEditUser({
                          ...editUser,
                          jobTitleId: v === '__none__' ? '' : v,
                          jobTitle: v === '__none__' ? '' : (jt?.label || ''),
                          departement: v !== '__none__' && !editUser.departement && jt?.department
                            ? jt.department
                            : editUser.departement,
                        });
                      }}
                    >
                      <SelectTrigger data-testid="edit-staff-jobtitle-select">
                        <SelectValue placeholder="Sélectionner un poste" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="__none__">Poste à renseigner</SelectItem>
                        {jobTitlesForEdit.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.label}{t.department ? ` · ${t.department}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Le poste décrit la fonction réelle ; il n&apos;a aucun effet sur les droits d&apos;accès.
                      La liste se gère dans Paramètres → Postes.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Responsable hiérarchique</Label>
                    <Select
                      value={editUser.manager_id || '__none__'}
                      onValueChange={(v) => setEditUser({ ...editUser, manager_id: v === '__none__' ? '' : v })}
                    >
                      <SelectTrigger data-testid="edit-staff-manager-select">
                        <SelectValue placeholder="Sélectionner un responsable" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="__none__">Aucun (rattaché à la direction)</SelectItem>
                        {users
                          .filter((u) => u.userType === 'internal' && u.id !== editUser.id)
                          .map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.firstName} {u.lastName}
                              {u.jobTitle ? ` · ${u.jobTitle}` : ''}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Définit « qui reporte à qui » dans l&apos;organigramme.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Rôle (droits d&apos;accès)</Label>
                    <div className="flex items-center gap-2">
                      {getRoleBadge(editUser.displayRole || editUser.role, 'internal')}
                      <span className="text-xs text-gray-500">modifiable depuis la colonne Rôle de la liste</span>
                    </div>
                  </div>
                </>
              )}

              {/* Type de compte - Particulier / Entreprise */}
              {editUser.userType === 'client' && (
                <div className="space-y-2">
                  <Label>Type de compte</Label>
                  <div className="flex gap-2">
                    <Button type="button" variant={editUser.accountType !== 'entreprise' ? 'default' : 'outline'} className={editUser.accountType !== 'entreprise' ? 'bg-[#1db584] hover:bg-[#19a074]' : ''} onClick={() => setEditUser({...editUser, accountType: 'particulier', companyName: ''})}>
                      Particulier
                    </Button>
                    <Button type="button" variant={editUser.accountType === 'entreprise' ? 'default' : 'outline'} className={editUser.accountType === 'entreprise' ? 'bg-[#1db584] hover:bg-[#19a074]' : ''} onClick={() => setEditUser({...editUser, accountType: 'entreprise'})}>
                      Entreprise
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Champ Entreprise - Visible quand type entreprise ou pour chauffeur */}
              {(editUser.accountType === 'entreprise' || editUser.userType === 'driver') && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Entreprise
                </Label>
                <div className="relative">
                  <Input 
                    value={companySearchQuery || editUser.companyName || ''} 
                    onChange={(e) => {
                      setCompanySearchQuery(e.target.value);
                      setShowCompanyDropdown(true);
                    }}
                    onFocus={() => setShowCompanyDropdown(true)}
                    placeholder="Rechercher ou créer une entreprise..."
                  />
                  {showCompanyDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                      {/* Filtrer les entreprises */}
                      {companies
                        .filter(c => c && c.toLowerCase().includes((companySearchQuery || '').toLowerCase()))
                        .slice(0, 10)
                        .map((company, idx) => (
                          <div 
                            key={idx} 
                            className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${editUser.companyName === company ? 'bg-[#1db584]/10 text-[#1db584]' : ''}`}
                            onClick={() => {
                              setEditUser({...editUser, companyName: company});
                              setCompanySearchQuery('');
                              setShowCompanyDropdown(false);
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <Building className="w-4 h-4 text-gray-400" />
                              {company}
                            </div>
                          </div>
                        ))
                      }
                      {/* Option pour créer une nouvelle entreprise */}
                      {companySearchQuery && !companies.some(c => c?.toLowerCase() === companySearchQuery.toLowerCase()) && (
                        <div 
                          className="px-3 py-2 cursor-pointer hover:bg-green-50 border-t"
                          onClick={() => {
                            setEditUser({...editUser, companyName: companySearchQuery});
                            setShowCompanyDropdown(false);
                          }}
                        >
                          <div className="flex items-center gap-2 text-[#1db584]">
                            <Plus className="w-4 h-4" />
                            Créer &quot;{companySearchQuery}&quot;
                          </div>
                        </div>
                      )}
                      {/* Option pour supprimer l'entreprise */}
                      {editUser.companyName && (
                        <div 
                          className="px-3 py-2 cursor-pointer hover:bg-red-50 border-t text-red-600"
                          onClick={() => {
                            setEditUser({...editUser, companyName: ''});
                            setCompanySearchQuery('');
                            setShowCompanyDropdown(false);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <X className="w-4 h-4" />
                            Retirer l&apos;entreprise
                          </div>
                        </div>
                      )}
                      {companies.length === 0 && !companySearchQuery && (
                        <div className="px-3 py-2 text-gray-500 text-sm">
                          Aucune entreprise. Tapez pour en créer une.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {editUser.companyName && (
                  <p className="text-xs text-gray-500">Entreprise actuelle: {editUser.companyName}</p>
                )}
              </div>
              )}

              {/* Section Emails de notification supplémentaires */}
              <div className="space-y-3 pt-2 border-t">
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Emails de notification supplémentaires
                </Label>
                <p className="text-xs text-gray-500">
                  Ces adresses recevront une copie des notifications importantes (opérations, factures, etc.)
                </p>
                
                {/* Liste des emails existants */}
                {loadingEmails ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Chargement...
                  </div>
                ) : (
                  <div className="space-y-2">
                    {additionalEmails.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {additionalEmails.map((email, idx) => (
                          <Badge 
                            key={idx} 
                            variant="secondary" 
                            className="flex items-center gap-1 px-2 py-1"
                          >
                            <Mail className="w-3 h-3" />
                            {email}
                            <button
                              type="button"
                              onClick={() => handleRemoveNotificationEmail(email)}
                              className="ml-1 hover:text-red-500"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">Aucun email supplémentaire configuré</p>
                    )}
                  </div>
                )}
                
                {/* Formulaire d'ajout */}
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={newNotificationEmail}
                    onChange={(e) => setNewNotificationEmail(e.target.value)}
                    placeholder="Ajouter un email..."
                    className="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddNotificationEmail();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddNotificationEmail}
                    disabled={!newNotificationEmail}
                    className="border-[#1db584] text-[#1db584] hover:bg-[#1db584]/10"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {additionalEmails.length >= 10 && (
                  <p className="text-xs text-orange-500">Maximum 10 emails supplémentaires</p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>Annuler</Button>
            <Button onClick={handleEditUser} disabled={bulkActionLoading} className="bg-[#1db584] hover:bg-[#19a074]">
              {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Type Modal */}
      <Dialog open={isChangeTypeModalOpen} onOpenChange={setIsChangeTypeModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-purple-600" />
              Changer le type d&apos;utilisateur
            </DialogTitle>
            <DialogDescription>
              {changeTypeUser && `Convertir ${changeTypeUser.firstName} ${changeTypeUser.lastName} (${USER_TYPES[changeTypeUser.userType]?.label})`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nouveau type</Label>
              <Select value={newType} onValueChange={setNewType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['client', 'internal', 'driver'].filter(t => t !== changeTypeUser?.userType).map(type => (
                    <SelectItem key={type} value={type}>{USER_TYPES[type].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {newType !== 'driver' && (
              <div className="space-y-2">
                <Label>Nouveau rôle (optionnel)</Label>
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner un rôle" /></SelectTrigger>
                  <SelectContent>
                    {(dynamicRolesByType[newType] || []).map(r => {
                      const Ic = r.Icon || User;
                      return (
                        <SelectItem key={r.value} value={r.value}>
                          <span className="inline-flex items-center gap-2">
                            <Ic className="w-3.5 h-3.5 text-slate-500" />
                            {r.label}
                            {!r.isSystem && (
                              <span className="ml-1 px-1 text-[9px] uppercase rounded bg-violet-100 text-violet-700">Custom</span>
                            )}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
              ⚠️ Cette action va créer un nouveau compte dans la collection cible et supprimer l&apos;ancien. Les données de base seront conservées.
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsChangeTypeModalOpen(false)}>Annuler</Button>
            <Button onClick={handleChangeType} disabled={bulkActionLoading} className="bg-purple-600 hover:bg-purple-700">
              {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Convertir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Status Dialog */}
      <AlertDialog open={showBulkStatusDialog} onOpenChange={setShowBulkStatusDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{bulkNewStatus ? 'Activer' : 'Désactiver'} les utilisateurs</AlertDialogTitle>
            <AlertDialogDescription>
              Vous allez {bulkNewStatus ? 'activer' : 'désactiver'} {selectedUsers.length} utilisateur(s).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleBulkStatusChange(bulkNewStatus)} disabled={bulkActionLoading} className={bulkNewStatus ? 'bg-green-600' : 'bg-orange-600'}>
              {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Role Change Dialog */}
      <AlertDialog open={showBulkRoleDialog} onOpenChange={setShowBulkRoleDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Changer le rôle</AlertDialogTitle>
            <AlertDialogDescription>
              Changer le rôle de {selectedUsers.length} utilisateur(s).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label>Nouveau rôle</Label>
            <Select value={bulkNewRole} onValueChange={setBulkNewRole}>
              <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                {dynamicRoles.map(role => {
                  const Ic = role.Icon || User;
                  return (
                    <SelectItem key={role.value} value={role.value}>
                      <span className="inline-flex items-center gap-2">
                        <Ic className="w-3.5 h-3.5 text-slate-500" />
                        {role.label}
                        {!role.isSystem && (
                          <span className="ml-1 px-1 text-[9px] uppercase rounded bg-violet-100 text-violet-700">Custom</span>
                        )}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkRoleChange} disabled={bulkActionLoading} className="bg-purple-600">
              {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer les utilisateurs</AlertDialogTitle>
            <AlertDialogDescription>
              Vous allez supprimer {selectedUsers.length} utilisateur(s). Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} disabled={bulkActionLoading} className="bg-red-600">
              {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Reset Dialog */}
      <AlertDialog open={showBulkResetDialog} onOpenChange={(open) => {
        setShowBulkResetDialog(open);
        if (!open) {
          setUseBulkCustomPassword(false);
          setBulkCustomPassword('');
          setSendEmailAfterReset(false);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Réinitialiser les mots de passe</AlertDialogTitle>
            <AlertDialogDescription>
              Vous allez réinitialiser les mots de passe de {selectedUsers.length} utilisateur(s).
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="useBulkCustom" 
                checked={useBulkCustomPassword} 
                onCheckedChange={(c) => setUseBulkCustomPassword(c)} 
              />
              <Label htmlFor="useBulkCustom" className="text-sm">Définir un mot de passe personnalisé pour tous</Label>
            </div>
            
            {useBulkCustomPassword && (
              <Input 
                type="password" 
                placeholder="Nouveau mot de passe (min. 6 caractères)"
                value={bulkCustomPassword}
                onChange={(e) => setBulkCustomPassword(e.target.value)}
              />
            )}
            
            <div className="flex items-center space-x-2 pt-2 border-t">
              <Checkbox 
                id="sendEmailAfterReset" 
                checked={sendEmailAfterReset} 
                onCheckedChange={(c) => setSendEmailAfterReset(c)} 
              />
              <Label htmlFor="sendEmailAfterReset" className="text-sm">
                📧 Envoyer le nouveau mot de passe par email aux utilisateurs
              </Label>
            </div>
            
            {!useBulkCustomPassword && (
              <p className="text-sm text-muted-foreground">
                Un mot de passe aléatoire sera généré pour chaque utilisateur.
              </p>
            )}
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkResetPasswords} 
              disabled={bulkActionLoading || (useBulkCustomPassword && bulkCustomPassword.length < 6)} 
              className="bg-blue-600"
            >
              {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-4 h-4 mr-2" />Réinitialiser</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Reset Results Dialog */}
      <Dialog open={showBulkResetResultsDialog} onOpenChange={(open) => {
        setShowBulkResetResultsDialog(open);
        if (!open) setBulkResetResults(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-green-600" />
              Mots de passe réinitialisés
            </DialogTitle>
            <DialogDescription>
              {bulkResetResults?.results?.length || 0} utilisateur(s) traité(s)
              {bulkResetResults?.emailsSent > 0 && ` • ${bulkResetResults.emailsSent} email(s) envoyé(s)`}
              {bulkResetResults?.emailsFailed > 0 && ` • ${bulkResetResults.emailsFailed} échec(s)`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex gap-2 py-2">
            <Button size="sm" variant="outline" onClick={downloadBulkResetCSV}>
              <Download className="w-4 h-4 mr-2" />
              Télécharger CSV
            </Button>
            <Button size="sm" variant="outline" onClick={copyAllPasswordsToClipboard}>
              <Copy className="w-4 h-4 mr-2" />
              Copier tout
            </Button>
          </div>
          
          <div className="flex-1 overflow-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left p-2 font-medium">Email</th>
                  <th className="text-left p-2 font-medium">Nom</th>
                  <th className="text-left p-2 font-medium">Mot de passe</th>
                  <th className="text-left p-2 font-medium">Email</th>
                </tr>
              </thead>
              <tbody>
                {bulkResetResults?.results?.map((r, idx) => (
                  <tr key={idx} className="border-t hover:bg-gray-50">
                    <td className="p-2 text-xs">{r.email}</td>
                    <td className="p-2 text-xs">{r.name}</td>
                    <td className="p-2">
                      <div className="flex items-center gap-1">
                        <code className="text-xs bg-gray-100 px-1 py-0.5 rounded font-mono">{r.newPassword}</code>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => {
                          navigator.clipboard.writeText(r.newPassword);
                          toast({ title: 'Copié' });
                        }}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                    <td className="p-2">
                      {r.emailSent ? (
                        <span className="text-green-600 text-xs flex items-center gap-1"><UserCheck className="w-3 h-3" /> Envoyé</span>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowBulkResetResultsDialog(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single Password Reset Modal */}
      <Dialog open={showResetPasswordModal} onOpenChange={setShowResetPasswordModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-blue-600" />
              Réinitialiser le mot de passe
            </DialogTitle>
            <DialogDescription>
              {resetPasswordUser && `${resetPasswordUser.firstName} ${resetPasswordUser.lastName} (${resetPasswordUser.email})`}
            </DialogDescription>
          </DialogHeader>
          
          {!resetPasswordResult ? (
            <div className="py-4 space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="useCustom" 
                  checked={useCustomPassword} 
                  onCheckedChange={(c) => setUseCustomPassword(c)} 
                />
                <Label htmlFor="useCustom" className="text-sm">Définir un mot de passe personnalisé</Label>
              </div>
              
              {useCustomPassword && (
                <Input 
                  type="password" 
                  placeholder="Nouveau mot de passe (min. 6 caractères)"
                  value={customPassword}
                  onChange={(e) => setCustomPassword(e.target.value)}
                />
              )}
              
              {!useCustomPassword && (
                <p className="text-sm text-gray-500">Un mot de passe aléatoire sécurisé sera généré.</p>
              )}
            </div>
          ) : (
            <div className="py-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-green-800 font-medium mb-2 flex items-center gap-1"><UserCheck className="w-4 h-4" /> Mot de passe réinitialisé</p>
                <div className="flex items-center gap-2 mt-3">
                  <code className="flex-1 p-2 bg-white rounded border text-sm font-mono">
                    {resetPasswordResult.newPassword}
                  </code>
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(resetPasswordResult.newPassword)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            {!resetPasswordResult ? (
              <>
                <Button variant="outline" onClick={() => setShowResetPasswordModal(false)}>Annuler</Button>
                <Button onClick={handleResetPassword} disabled={bulkActionLoading || (useCustomPassword && customPassword.length < 6)} className="bg-blue-600">
                  {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Réinitialiser'}
                </Button>
              </>
            ) : (
              <Button onClick={() => { setShowResetPasswordModal(false); setResetPasswordResult(null); }}>Fermer</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnifiedUsersManagement;
